import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import { api, money, errMsg } from "@/lib/api";
import { InlineLoader, StatusPill } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, Loader2, AlertCircle, ArrowLeft, ArrowRight, Eye, Clock } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import GalleryUpload from "@/components/GalleryUpload";
import VideoUpload from "@/components/VideoUpload";
import MatchManager from "@/components/MatchManager";

const STEPS = ["Story", "Beneficiary", "Upload", "Review"];

export default function CampaignBuilder() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!campaignId) return;
    api.get("/campaigns/mine").then((r) => {
      const found = r.data.find((x) => x.id === campaignId);
      if (found) setC(found);
      else setLoadError("Campaign not found");
    }).catch((e) => setLoadError(errMsg(e)));
  }, [campaignId]);

  if (!campaignId) return <Navigate to="/start-a-fundraiser" replace />;
  if (loadError) return <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{loadError}</AlertDescription></Alert>;
  if (!c) return <InlineLoader label="Loading campaign" />;

  const set = (k, v) => setC((prev) => ({ ...prev, [k]: v }));

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/campaigns/${campaignId}`, patch);
      setC(data);
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
    finally { setSaving(false); }
  };

  const validateStep = () => {
    if (step === 0) {
      if (!(c.title || "").trim()) return "Please enter a campaign title.";
      if ((c.story || "").trim().length < 30) return "Please tell your story (at least 30 characters) so donors understand your cause.";
    }
    if (step === 1) {
      if (!(c.beneficiary_name || "").trim()) return "Please enter the beneficiary's name.";
      if (!(c.relationship || "").trim()) return "Please describe your relationship to the beneficiary.";
      if ((c.intended_use || "").trim().length < 15) return "Please describe how the funds will be used.";
    }
    if (step === 2) {
      if (!(c.cover_image || "").trim()) return "Please upload a cover image for your campaign.";
    }
    return "";
  };

  const next = async () => {
    const msg = validateStep();
    if (msg) { setError(msg); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setError("");
    const patches = [
      { title: c.title, story: c.story, category: c.category, goal_amount: c.goal_amount ? Number(c.goal_amount) : null, is_team: !!c.is_team, text_keyword: (c.text_keyword || "").trim() || null, giving_tiers: (c.giving_tiers || []).filter((t) => t && Number(t.amount) > 0) },
      { beneficiary_name: c.beneficiary_name, relationship: c.relationship, intended_use: c.intended_use },
      { cover_image: c.cover_image, gallery: c.gallery || [], video_url: c.video_url || "", use_video_cover: !!c.use_video_cover },
    ];
    if (step < 3) {
      const ok = await save(patches[step]);
      if (ok) setStep(step + 1);
    }
  };

  const submit = async () => {
    if (!c.truthfulness_attested || !c.terms_accepted) return setError("Please confirm the attestations to submit.");
    setSaving(true);
    setError("");
    try {
      await api.patch(`/campaigns/${campaignId}`, {
        truthfulness_attested: c.truthfulness_attested, terms_accepted: c.terms_accepted,
        cover_image: c.cover_image,
      });
      await api.post(`/campaigns/${campaignId}/submit`);
      toast.success("Submitted for review!");
      navigate(`/organizer/campaign-status/${campaignId}`);
    } catch (e) { setError(errMsg(e)); }
    finally { setSaving(false); }
  };

  const editable = ["draft", "needs_info", "rejected"].includes(c.status);

  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">Campaign Builder</h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-ff-medium">
            <StatusPill status={c.status} />
            {!editable && <span>This campaign can no longer be edited.</span>}
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate(`/fundraisers/${c.slug}`)} data-testid="builder-preview"
          className="border-ff-blue text-ff-blue hover:bg-ff-blue hover:text-white">
          <Eye className="mr-2 h-4 w-4" /> Preview
        </Button>
      </div>

      {/* Stepper */}
      <div className="mb-6 flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex flex-1 items-center">
            <button onClick={() => setStep(i)} data-testid={`builder-step-${i}`}
              className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold ${i === step ? "bg-ff-blue text-white" : i < step ? "bg-[#ECFDF3] text-ff-green" : "bg-[#F3F4F6] text-ff-medium"}`}>
              {i < step ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span>{i + 1}</span>} {s}
            </button>
            {i < STEPS.length - 1 && <div className="mx-1 h-0.5 flex-1 bg-ff-border" />}
          </div>
        ))}
      </div>

      {error && <Alert variant="destructive" className="mb-4"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}

      <div className="rounded-2xl border border-ff-border bg-white p-8 shadow-sm ff-fade-up" key={step}>
        {step === 0 && (
          <div className="space-y-4" data-testid="builder-panel-story">
            <Field label="Campaign title" required><Input data-testid="builder-title" value={c.title || ""} disabled={!editable} onChange={(e) => set("title", e.target.value)} /></Field>
            <Field label="Your story" required><Textarea data-testid="builder-story" rows={8} value={c.story || ""} disabled={!editable} onChange={(e) => set("story", e.target.value)} placeholder="Tell donors who you're raising for and why…" /></Field>
            <Field label="Fundraising goal (optional)"><Input type="number" data-testid="builder-goal" value={c.goal_amount || ""} disabled={!editable} onChange={(e) => set("goal_amount", e.target.value)} /></Field>
            <Field label="Text-to-Give keyword (optional)">
              <Input data-testid="builder-text-keyword" value={c.text_keyword || ""} disabled={!editable}
                onChange={(e) => set("text_keyword", e.target.value.replace(/\s+/g, "").toUpperCase())}
                placeholder="e.g. MARIA" />
              <p className="mt-1 text-xs text-ff-medium">A short, memorable code donors can text to your FightFund number to get a donation link. Leave blank to use your campaign's web address as the code.</p>
            </Field>
            <label className="flex cursor-pointer items-center gap-2.5 text-sm text-ff-dark">
              <Checkbox data-testid="builder-is-team" checked={!!c.is_team} disabled={!editable} onCheckedChange={(v) => set("is_team", !!v)} />
              Make this a team fundraiser (add members &amp; a leaderboard from the campaign status page)
            </label>

            <div className="rounded-xl border border-ff-border bg-[#F9FAFB] p-4" data-testid="builder-giving-tiers">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-ff-blue">Suggested giving levels <span className="font-normal text-ff-medium">(optional)</span></Label>
                  <p className="mt-0.5 text-xs text-ff-medium">Show donors what their gift does. Levels appear as one-tap options on your donate page.</p>
                </div>
              </div>
              <div className="mt-3 space-y-2">
                {(c.giving_tiers || []).map((t, i) => (
                  <div key={i} className="grid grid-cols-[90px_1fr_auto] gap-2" data-testid={`tier-row-${i}`}>
                    <Input type="number" min="1" placeholder="$ amt" value={t.amount || ""} disabled={!editable}
                      onChange={(e) => set("giving_tiers", (c.giving_tiers || []).map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} />
                    <Input placeholder="What it funds, e.g. 'Feeds a family for a week'" value={t.description || ""} disabled={!editable}
                      onChange={(e) => set("giving_tiers", (c.giving_tiers || []).map((x, j) => j === i ? { ...x, description: e.target.value } : x))} />
                    <Button type="button" variant="ghost" size="sm" disabled={!editable} data-testid={`tier-remove-${i}`}
                      onClick={() => set("giving_tiers", (c.giving_tiers || []).filter((_, j) => j !== i))} className="text-ff-red">✕</Button>
                  </div>
                ))}
                {(c.giving_tiers || []).length < 5 && (
                  <Button type="button" variant="outline" size="sm" disabled={!editable} data-testid="tier-add"
                    onClick={() => set("giving_tiers", [...(c.giving_tiers || []), { amount: "", description: "" }])}
                    className="border-ff-blue text-ff-blue hover:bg-ff-blue hover:text-white">+ Add giving level</Button>
                )}
              </div>
            </div>

            <MatchManager campaignId={campaignId} match={c.match} onUpdate={(m) => set("match", m)} />
          </div>
        )}
        {step === 1 && (
          <div className="space-y-4" data-testid="builder-panel-beneficiary">
            <Field label="Beneficiary name" required><Input data-testid="builder-beneficiary" value={c.beneficiary_name || ""} disabled={!editable} onChange={(e) => set("beneficiary_name", e.target.value)} /></Field>
            <Field label="Your relationship to the beneficiary" required><Input data-testid="builder-relationship" value={c.relationship || ""} disabled={!editable} onChange={(e) => set("relationship", e.target.value)} placeholder="e.g. Sister, Coach, Self" /></Field>
            <Field label="Intended use of funds" required><Textarea data-testid="builder-intended-use" rows={4} value={c.intended_use || ""} disabled={!editable} onChange={(e) => set("intended_use", e.target.value)} /></Field>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-6" data-testid="builder-panel-media">
            <Field label="Cover image" required>
              <ImageUpload value={c.cover_image || ""} disabled={!editable} onChange={(url) => set("cover_image", url)} testid="builder-cover-upload" />
            </Field>

            <div>
              <Label className="text-ff-blue">Photo gallery <span className="font-normal text-ff-medium">(optional)</span></Label>
              <p className="mb-1.5 mt-0.5 text-xs text-ff-medium">Add extra photos to tell your story. They appear below the cover on your campaign page.</p>
              <GalleryUpload value={c.gallery || []} disabled={!editable} onChange={(g) => set("gallery", g)} testid="builder-gallery-upload" />
            </div>

            <div>
              <Label className="text-ff-blue">Campaign video <span className="font-normal text-ff-medium">(optional)</span></Label>
              <p className="mb-1.5 mt-0.5 text-xs text-ff-medium">A short video builds trust. All videos are manually reviewed for inappropriate content before they appear publicly.</p>
              <VideoUpload value={c.video_url || ""} disabled={!editable} onChange={(url) => set("video_url", url)} testid="builder-video-upload" />
              {c.video_url && (
                <>
                  {c.video_status === "pending_review" && (
                    <div className="mt-2 flex items-center gap-2 rounded-lg bg-[#FFF7ED] px-3 py-2 text-xs font-semibold text-ff-amber" data-testid="video-status-pending">
                      <Clock className="h-3.5 w-3.5" /> Your video is awaiting review and will appear once approved.
                    </div>
                  )}
                  {c.video_status === "approved" && (
                    <div className="mt-2 flex items-center gap-2 rounded-lg bg-[#ECFDF5] px-3 py-2 text-xs font-semibold text-ff-green" data-testid="video-status-approved">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Video approved.
                    </div>
                  )}
                  {c.video_status === "rejected" && (
                    <div className="mt-2 rounded-lg bg-[#FEF2F2] px-3 py-2 text-xs font-semibold text-ff-red" data-testid="video-status-rejected">
                      <span className="flex items-center gap-2"><AlertCircle className="h-3.5 w-3.5" /> Video not approved.</span>
                      {c.video_reject_reason && <span className="mt-1 block font-normal">{c.video_reject_reason}</span>}
                    </div>
                  )}
                  <label className="mt-3 flex cursor-pointer items-center gap-2.5 text-sm text-ff-dark">
                    <Checkbox data-testid="builder-use-video-cover" checked={!!c.use_video_cover} disabled={!editable}
                      onCheckedChange={(v) => set("use_video_cover", !!v)} />
                    Use this video as my campaign cover (shown once approved)
                  </label>
                </>
              )}
            </div>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-5" data-testid="builder-panel-review">
            <h3 className="font-heading text-lg font-bold text-ff-blue">Review &amp; submit</h3>
            <Alert className="border-ff-amber/40 bg-[#FFF7ED]" data-testid="builder-verify-notice">
              <AlertCircle className="h-4 w-4 text-ff-amber" />
              <AlertDescription className="text-ff-dark">
                Before you can submit, you must complete <b>phone, email, and payout verification</b>. Finish these on your{" "}
                <Link to="/organizer/onboarding" className="font-semibold text-ff-blue underline">Payout Onboarding</Link> page.
                New fundraisers are also scanned for duplicated content and may be placed under review.
              </AlertDescription>
            </Alert>
            <div className="grid gap-3 rounded-xl border border-ff-border bg-[#F3F4F6] p-4 text-sm">
              <Row label="Title" value={c.title} />
              <Row label="Category" value={c.category} />
              <Row label="Goal" value={c.goal_amount ? money(c.goal_amount) : "No fixed goal"} />
              <Row label="Beneficiary" value={c.beneficiary_name || "—"} />
              <Row label="Relationship" value={c.relationship || "—"} />
            </div>
            <label className="flex cursor-pointer items-start gap-3 text-sm text-ff-dark">
              <Checkbox data-testid="builder-attest-truth" checked={!!c.truthfulness_attested} disabled={!editable}
                onCheckedChange={(v) => set("truthfulness_attested", !!v)} />
              I attest that the information in this campaign is truthful and accurate.
            </label>
            <label className="flex cursor-pointer items-start gap-3 text-sm text-ff-dark">
              <Checkbox data-testid="builder-attest-terms" checked={!!c.terms_accepted} disabled={!editable}
                onCheckedChange={(v) => set("terms_accepted", !!v)} />
              I accept FightFund's Terms and understand campaigns are reviewed before accepting public donations.
            </label>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="builder-back">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          {step < 3 ? (
            <Button onClick={next} disabled={saving || !editable} data-testid="builder-next" className="bg-ff-blue text-white hover:bg-ff-blue/90">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Save &amp; continue <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          ) : (
            <Button onClick={submit} disabled={saving || !editable} data-testid="builder-submit" className="bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting</> : "Submit for review"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return <div><Label className="text-ff-blue">{label}{required && <span className="text-ff-red"> *</span>}</Label><div className="mt-1.5">{children}</div></div>;
}
function Row({ label, value }) {
  return <div className="flex justify-between gap-4"><span className="text-ff-medium">{label}</span><span className="text-right font-semibold text-ff-blue">{value}</span></div>;
}
